import React, { useState, createContext } from "react";
import { StateNavigator, HTML5HistoryManager } from "navigation";
import { NavigationHandler, SceneView } from "navigation-react";
import LoaderAnimation from "./LoaderAnimation";
import Login from "./Login";
import SalesOrder from "./SalesOrder";
import ViewSalesRegister from "./ViewSalesRegister";
import ViewSalesInvoiceDetails from "./ViewSalesInvoiceDetails";
import ProductRegister from "./ProductRegister";
import ReceiveStock from "./ReceiveStock";
import StockInventory from "./StockInventory";
import EditSalesOrder from "./EditSalesOrder";
import CustomerRegister from "./CustomerRegister";
import AddCTScanRequestRegister from "./AddCTScanRequestRegister";
import StaffSignRegister from "./StaffSignRegister";
import DashboardContent from "./DashboardContent";
import DeliveriesRegister from "./DeliveriesRegister";
import VisitorSignRegisterDetails from "./VisitorSignRegisterDetails";
import VisitorEditSignRegister from "./VisitorEditSignRegister";
import DeliveriesEditRegister from "./DeliveriesEditRegister";
import StaffRegister from "./StaffRegister";
import StaffEditRegister from "./StaffEditRegister";
import StaffAddRegister from "./StaffAddRegister";
import OnlineOrders from "./OnlineOrders";
import { VisitorDetailsProvider } from "./VisitorDetailsContext";
import SecureLS from "secure-ls";
import "./styles.css";
import ItemizedSalesRegister from "./ItemizedSalesRegister";

const ls = new SecureLS({ encodingType: "aes" });

// ✅ Role Context with setter
export const RoleContext = createContext({ roles: [], setRoles: () => {} });

// Navigation setup
const stateNavigator = new StateNavigator(
  [
    { key: "login", route: "login" },
    { key: "salesorder", route: "sales-order" },
    { key: "viewsalesregister", route: "view-sales-register" },
    { key: "viewsalesinvoicedetails", route: "view-sales-invoice-details" },
    { key: "productregister", route: "product-register" },
    { key: "receivestock", route: "receive-stock" },
    { key: "stockinventory", route: "stock-inventory" },
    { key: "editsalesorder", route: "edit-sales-order" },
    { key: "itemizedsalesregister", route: "itemized-sales-register" },
    { key: "customerregister", route: "customer-register" },
    { key: "addctscanrequestregister", route: "add-ct-scan-request-register" },
    { key: "staffsignregister", route: "staff-sign-register" },
    { key: "dashboardcontent", route: "dashboard-content" },
    { key: "deliveriesregister", route: "deliveries-register" },
    { key: "deliverieseditregister", route: "deliveries-edit-register" },
    { key: "staffregister", route: "staff-register" },
    { key: "staffeditregister", route: "staff-edit-register" },
    { key: "staffaddregister", route: "staff-add-register" },
    { key: "onlineorders", route: "online-orders" },

    {
      key: "visitorsignregisterdetails",
      route: "visitor-signregister-details",
    },
    { key: "visitoreditsignregister", route: "visitor-editsign-register" },
  ],
  new HTML5HistoryManager(),
);

// Automatically navigate root `/` to `/login`
if (!window.location.pathname || window.location.pathname === "/") {
  stateNavigator.navigate("login", { replace: true });
}

stateNavigator.start();

const App = () => {
  const [roles, setRoles] = useState(ls.get("currentuserrole") || []);

  return (
    <RoleContext.Provider value={{ roles, setRoles }}>
      <VisitorDetailsProvider>
        <NavigationHandler stateNavigator={stateNavigator}>
          <SceneView active="login">
            <Login />
          </SceneView>

          <SceneView active="viewsalesregister">
            <ViewSalesRegister />
          </SceneView>

          <SceneView active="salesorder">
            <SalesOrder />
          </SceneView>

          <SceneView active="viewsalesinvoicedetails">
            <ViewSalesInvoiceDetails />
          </SceneView>

          <SceneView active="productregister">
            <ProductRegister />
          </SceneView>

          <SceneView active="receivestock">
            <ReceiveStock />
          </SceneView>

          <SceneView active="stockinventory">
            <StockInventory />
          </SceneView>

          <SceneView active="editsalesorder">
            <EditSalesOrder />
          </SceneView>

          <SceneView active="itemizedsalesregister">
            <ItemizedSalesRegister />
          </SceneView>

          <SceneView active="customerregister">
            <CustomerRegister />
          </SceneView>

          <SceneView active="addctscanrequestregister">
            <AddCTScanRequestRegister />
          </SceneView>

          <SceneView active="dashboardcontent">
            <DashboardContent />
          </SceneView>

          <SceneView active="staffsignregister">
            <StaffSignRegister />
          </SceneView>

          <SceneView active="deliveriesregister">
            <DeliveriesRegister />
          </SceneView>

          <SceneView active="visitorsignregisterdetails">
            <VisitorSignRegisterDetails />
          </SceneView>

          <SceneView active="visitoreditsignregister">
            <VisitorEditSignRegister />
          </SceneView>

          <SceneView active="deliverieseditregister">
            <DeliveriesEditRegister />
          </SceneView>

          <SceneView active="staffregister">
            <StaffRegister />
          </SceneView>

          <SceneView active="staffeditregister">
            <StaffEditRegister />
          </SceneView>

          <SceneView active="staffaddregister">
            <StaffAddRegister />
          </SceneView>
          <SceneView active="onlineorders">
            <OnlineOrders />
          </SceneView>

          <SceneView active="root">
            <LoaderAnimation loadingTxt="Initializing..." />
          </SceneView>
        </NavigationHandler>
      </VisitorDetailsProvider>
    </RoleContext.Provider>
  );
};

export default App;
